import { useCallback, useMemo, useRef, useState } from "react";
import { Divider, Form, FormInstance, notification, RadioChangeEvent, Row, Tabs } from "antd";
import { useSelector } from "react-redux";

import { IOrgao } from "../../../../Orgaos/interfaces";
import { IUpdateSistema } from "./components/GerenciarSistemaContabil/interfaces";
import { IConfiguracaoContabil, ISistemaContabil, ISolicitacaoAtivacao } from "../../../interfaces";
import { IUpdateSistemaContabilIntegrado, IUpdateTipoEmpenhosERestos } from "./components/interfaces";
import { ICreateOrgaoVinculado, IDeleteOrgaoVinculado, IUpdateOrgaoVinculado } from "./components/OrgaosVinculadosStep/interfaces";
import { ICreateConfiguracaoSistemaContabil, IDeleteConfiguracaoSistemaContabil, IUpdateConfiguracaoSistemaContabil } from "./components/ConfigurarSistemaContabil/interfaces";
import { stateGlobal } from "../../../../../../interfaces/Global";

import { GerenciarSistemaContabil } from "./components/GerenciarSistemaContabil";
import InputObservacaoEtapa from "../../components/InputObservacaoEtapa";
import { EmitirErro } from "../../../../../../services/EmitirErro";
import OrgaosVinculadosStep from "./components/OrgaosVinculadosStep";
import ConfigurarSistemaContabil from "./components/ConfigurarSistemaContabil";
import EtapaInfo from "../../components/EtapaInfo";
import RadioInput from "../../../../components/RadioInput";
import ConfigRow from "../../../../components/ConfigRow";

interface IProps {
    solicitacao: ISolicitacaoAtivacao | null;
    orgao: IOrgao | null;
    onEtapaIniciada: (solicitacao: ISolicitacaoAtivacao) => void;
    onEtapaConcluida: (solicitacao: ISolicitacaoAtivacao) => void;
    onUpdateObservacao: (solicitacao: ISolicitacaoAtivacao) => void;
    onSistemaContabilIntegradoChange: (value: boolean) => void;
    onTipoEmpenhosERestosChange: (value: string) => void;
    onUpdateSistemaContabil: (sistemaContabil: ISistemaContabil) => void;
    onUpdateOrgao: (orgao: IOrgao) => void;
    onConfiguracaoSistemaContabilChange: (configuracao: IConfiguracaoContabil | null) => void;
    onCreateOrgaoVinculado: (data: ICreateOrgaoVinculado) => Promise<IOrgao | null>;
    onUpdateOrgaoVinculado: (data: IUpdateOrgaoVinculado) => Promise<IOrgao | null>;
    onDeleteOrgaoVinculado: (data: IDeleteOrgaoVinculado) => Promise<IOrgao | null>;
    requestUpdateSistemaContabil: (data: IUpdateSistema) => Promise<ISistemaContabil | null>;
    onCreateConfiguracaoSistemaContabil: (data: ICreateConfiguracaoSistemaContabil) => Promise<IConfiguracaoContabil | null>;
    onUpdateConfiguracaoSistemaContabil: (data: IUpdateConfiguracaoSistemaContabil) => Promise<IConfiguracaoContabil | null>;
    onDeleteConfiguracaoSistemaContabil: (data: IDeleteConfiguracaoSistemaContabil) => Promise<void>;
    onUpdateTipoEmpenhosERestos: (data: IUpdateTipoEmpenhosERestos) => Promise<string>;
    onUpdateSistemaContabilIntegrado: (data: IUpdateSistemaContabilIntegrado) => Promise<boolean>;
    isManutencao?: boolean;
}

export const IntegracaoComSistemaContabil = ({
    solicitacao,
    onEtapaIniciada,
    onEtapaConcluida,
    onUpdateObservacao,
    onSistemaContabilIntegradoChange,
    onTipoEmpenhosERestosChange,
    onUpdateSistemaContabil,
    onUpdateOrgao,
    onConfiguracaoSistemaContabilChange,
    onCreateOrgaoVinculado,
    onUpdateOrgaoVinculado,
    onDeleteOrgaoVinculado,
    requestUpdateSistemaContabil,
    onCreateConfiguracaoSistemaContabil,
    onUpdateConfiguracaoSistemaContabil,
    onDeleteConfiguracaoSistemaContabil,
    onUpdateTipoEmpenhosERestos,
    onUpdateSistemaContabilIntegrado,
    orgao,
    isManutencao = false,
}: IProps) => {
    const [isSubmitingSistemaContabilIntegrado, setIsSubmitingSistemaContabilIntegrado] = useState(false);
    const [isSubmitingTipoEmpenhosERestos, setIsSubmitingTipoEmpenhosERestos] = useState(false);

    const sistemaContabilIntegradoValueBeingSubmited = useRef<boolean | null>(null);
    const tipoEmpenhosERestosValueBeingSubmited = useRef<string | null>(null);
    const empenhosERestosFormRef = useRef<FormInstance<any>>(null);
    const tipoEmpenhosERestosFormRef = useRef<FormInstance<any>>(null);
    const etapa = solicitacao?.etapasSolicitacaoAtivacao?.find((e) => e.etapa.ordem === 1);
    const propsState = useSelector((state: stateGlobal) => state.global);

    const handleChangeEmpenhosERestos = useCallback(async (e: RadioChangeEvent) => {
        try {
            if (!solicitacao) {
                return;
            }

            sistemaContabilIntegradoValueBeingSubmited.current = e.target.value;
            setIsSubmitingSistemaContabilIntegrado(true);

            const response = await onUpdateSistemaContabilIntegrado({
                solicitacaoId: solicitacao.id,
                integrado: e.target.value,
            });

            empenhosERestosFormRef.current?.setFieldsValue({ empenhos_e_restos: response });
            sistemaContabilIntegradoValueBeingSubmited.current = null;
            onSistemaContabilIntegradoChange(response);
            setIsSubmitingSistemaContabilIntegrado(false);
            notification.success({
                message: 'Sucesso',
                description: 'Status do sistema contábil alterado com sucesso!',
            });
        } catch (error) {
            EmitirErro(error, true, 'updateSistemaContabilIntegrado_23o90asdias92kmo9s');
            empenhosERestosFormRef.current?.setFieldsValue({ empenhos_e_restos: !sistemaContabilIntegradoValueBeingSubmited.current });
            sistemaContabilIntegradoValueBeingSubmited.current = null;
            setIsSubmitingSistemaContabilIntegrado(false);
        }
    }, [solicitacao]);

    const handleChangeTipoEmpenhosERestos = useCallback(async (e: RadioChangeEvent) => {
        try {
            if (!solicitacao) {
                return;
            }

            tipoEmpenhosERestosValueBeingSubmited.current = e.target.value;
            setIsSubmitingTipoEmpenhosERestos(true);

            const response = await onUpdateTipoEmpenhosERestos({
                solicitacaoId: solicitacao.id,
                tipo: e.target.value,
            });

            tipoEmpenhosERestosFormRef.current?.setFieldsValue({ tipo_empenhos_e_restos: response });
            tipoEmpenhosERestosValueBeingSubmited.current = null;
            onTipoEmpenhosERestosChange(response);
            setIsSubmitingTipoEmpenhosERestos(false);
            notification.success({
                message: 'Sucesso',
                description:
                    'Tipo padrão dos empenhos e restos alterado com sucesso!',
            });
        } catch (error) {
            EmitirErro(error, true, 'updateTipoEmpenhosERestos_23o90asdias92kmo9s');
            tipoEmpenhosERestosFormRef.current?.setFieldsValue({ tipo_empenhos_e_restos: tipoEmpenhosERestosValueBeingSubmited.current === "liquidado" ? 'nao_liquidado' : 'liquidado' });
            tipoEmpenhosERestosValueBeingSubmited.current = null;
            setIsSubmitingTipoEmpenhosERestos(false);
        }
    }, [solicitacao]);

    const items = useMemo(() => {
        if (!solicitacao) {
            return;
        }

        return [
            {
                key: 'orgaos-vinculados',
                label: 'Órgãos vinculados',
                children: (
                    <OrgaosVinculadosStep
                        onOrgaoChange={onUpdateOrgao}
                        orgao={orgao}
                        solicitacaoId={solicitacao.id}
                        onCreateOrgaoVinculado={onCreateOrgaoVinculado}
                        onUpdateOrgaoVinculado={onUpdateOrgaoVinculado}
                        onDeleteOrgaoVinculado={onDeleteOrgaoVinculado}
                        isManutencao={isManutencao}
                    />
                )
            },
            {
                key: 'configuracao-sistema-contabil',
                label: 'Configuração do sistema contábil',
                children: (
                    <>
                        <GerenciarSistemaContabil
                            solicitacao={solicitacao}
                            onUpdateSistema={onUpdateSistemaContabil}
                            requestUpdateSistema={requestUpdateSistemaContabil}
                        />

                        {
                            solicitacao.sistemaContabil.nome.toLowerCase() !== "outro" && (
                                <ConfigurarSistemaContabil
                                    onConfiguracaoSistemaContabilChange={onConfiguracaoSistemaContabilChange}
                                    onCreateConfiguracaoSistemaContabil={onCreateConfiguracaoSistemaContabil}
                                    onUpdateConfiguracaoSistemaContabil={onUpdateConfiguracaoSistemaContabil}
                                    onDeleteConfiguracaoSistemaContabil={onDeleteConfiguracaoSistemaContabil}
                                    solicitacao={solicitacao}
                                />
                            )
                        }

                        <div
                            style={{
                                border: `1px solid ${propsState.theme === 'light' ? '#0000000f' : '#ffffff1f'}`,
                                borderRadius: 8,
                                padding: '12px 16px',
                                marginTop: 32
                            }}
                        >
                            <ConfigRow
                                title="Tipo padrão dos empenhos e restos"
                                subTitle="Quando o cliente acessar a tela, esse será o filtro selecionado por padrão"
                            >
                                <Form
                                    ref={tipoEmpenhosERestosFormRef}
                                    style={{
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Form.Item
                                        initialValue={solicitacao.tipoEmpenhosERestos}
                                        name="tipo_empenhos_e_restos"
                                        style={{ margin: 0 }}
                                    >
                                        <RadioInput
                                            initialValue={solicitacao.tipoEmpenhosERestos}
                                            onChange={handleChangeTipoEmpenhosERestos}
                                            options={[
                                                {
                                                    value: 'nao_liquidado',
                                                    label: "Empenhados",
                                                },
                                                {
                                                    value: 'liquidado',
                                                    label: "Liquidados",
                                                },
                                            ]}
                                            isSubmiting={isSubmitingTipoEmpenhosERestos}
                                        />
                                    </Form.Item>
                                </Form>
                            </ConfigRow>

                            <Divider style={{ margin: '16px 0' }} />

                            <ConfigRow
                                title="Sistema contábil integrado"
                                subTitle='Define se as abas de "Resultado" e "Empenhos" aparecerão para o cliente'
                            >
                                <Form
                                    ref={empenhosERestosFormRef}
                                    style={{
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Form.Item
                                        initialValue={solicitacao.empenhosERestos}
                                        name="empenhos_e_restos"
                                        style={{ margin: 0 }}
                                    >
                                        <RadioInput
                                            initialValue={solicitacao.empenhosERestos}
                                            onChange={handleChangeEmpenhosERestos}
                                            options={[
                                                {
                                                    value: true,
                                                    label: "Sim",
                                                },
                                                {
                                                    value: false,
                                                    label: "Não",
                                                },
                                            ]}
                                            isSubmiting={isSubmitingSistemaContabilIntegrado}
                                        />
                                    </Form.Item>
                                </Form>
                            </ConfigRow>
                        </div>
                    </>
                )
            },
            !isManutencao ? (
                {
                    key: 'demais-configuracoes-etapa',
                    label: 'Demais configurações da etapa',
                    children: (
                        <>
                            <InputObservacaoEtapa
                                solicitacaoId={solicitacao.id}
                                etapa={etapa}
                                onUpdateObservacao={onUpdateObservacao}
                                style={{
                                    padding: 0,
                                }}
                            />
                        </>
                    )
                }
            ) : undefined,
        ].filter((e) => e !== undefined) as any[];
    }, [
        solicitacao,
        onEtapaConcluida,
        onUpdateObservacao,
        onUpdateSistemaContabil,
        onUpdateOrgao,
        onConfiguracaoSistemaContabilChange,
        isSubmitingSistemaContabilIntegrado,
        isSubmitingTipoEmpenhosERestos,
        handleChangeEmpenhosERestos,
        etapa,
        onCreateOrgaoVinculado,
        onUpdateOrgaoVinculado,
        onDeleteOrgaoVinculado,
        isManutencao,
        requestUpdateSistemaContabil,
    ]);

    if (!solicitacao) {
        return null;
    }

    return (
        <>
            <Row>
                {
                    !isManutencao && (
                        <EtapaInfo
                            iniciadaEm={etapa?.iniciadaEm}
                            concluidaEm={etapa?.concluidaEm}
                            descricao={etapa?.etapa.descricao}
                            solicitacaoId={solicitacao.id}
                            etapa={etapa}
                            onEtapaIniciada={onEtapaIniciada}
                            onEtapaConcluida={onEtapaConcluida}
                        />
                    )
                }

                <div
                    style={{
                        width: '100%',
                        ...(!isManutencao ? {
                            opacity: etapa?.iniciadaEm ? 1 : 0.5,
                            pointerEvents: etapa?.iniciadaEm ? 'all' : 'none'
                        } : {}),
                    }}
                >
                    <Tabs
                        type="line"
                        items={items}
                        style={{
                            margin: '0 10px',
                        }}
                    />
                </div>
            </Row>
        </>
    )
}
